import { AppCoreEnums } from '../shared/enums';
import { useCoreStore } from 'src/stores/core/store';
import useRouterUtils from '../router-utils/route-utils';
import updatePaginationStateData from '../composable/use-pagination-utils';
import { RouteLocationNormalized, RouteRecordNormalized } from 'vue-router';
import { IRouteMeta, IRouteMetaResolveResults, ResolveResult } from '../models/routing';
import useContainerDynamicFiltersAfterEachUiGuard from '../composable/containers/use-container-dynamic-filters-after-each-ui-guard';

export default (to: RouteLocationNormalized, from: RouteLocationNormalized) => {
  const toMeta = to.meta as IRouteMetaResolveResults;

  if (toMeta.isRouteCanceled) return;

  const initStoreForContainer = (containerType: AppCoreEnums.ContainerType, to: RouteLocationNormalized) => {
    const store = useCoreStore();
    const isNew = !!to.matched.find((x) => (x.meta as IRouteMeta)?.isNew);

    switch (containerType) {
      case AppCoreEnums.ContainerType.CollapsibleTableContainer:
        const { hasContainerChildRoutes, hasContainerChildInnerRoutes } = useRouterUtils();
        store.collapsibleTableContainerData.routeParams = to.params;

        store.setIsCollapsibleTableContainerLeftPanelCollapsed(
          hasContainerChildRoutes(to, AppCoreEnums.ContainerType.CollapsibleTableContainer) &&
            !hasContainerChildInnerRoutes(to, AppCoreEnums.ContainerType.TableInnerContainer) 
        );

        if (!hasContainerChildRoutes(to, AppCoreEnums.ContainerType.CollapsibleTableContainer)) {
          store.setIsCollapsibleTableContainerLeftPanelInMiniMode(false);
        }
        break;

      case AppCoreEnums.ContainerType.TabsContainer:
        store.tabsContainerData.lastActiveRouteName = isNew === true ? undefined : (to?.name as string);
        break;

      case AppCoreEnums.ContainerType.TableContainer:
        store.tableContainerData.routeParams = to.params;
        break;

      case AppCoreEnums.ContainerType.TableInnerContainer:
        store.tableInnerContainerData.routeParams = to.params;
        break;

      case AppCoreEnums.ContainerType.FormsContainer:
        store.formContainerData.isDirty = false;
        store.formContainerData.saveRequestCnt = 0;
        break;
      
       case AppCoreEnums.ContainerType.EmptyContainer:
        store.formContainerData.isDirty = false;
        store.formContainerData.saveRequestCnt = 0;
        break;
    }
  };

  const updateStoreBasedOnRouteResolveActionResults = (guardResolveResults: Map<RouteRecordNormalized, ResolveResult[]>) => {
    const { hasRouteContainer, hasRouteInnerContainer, allPaginationContainerTypes, allDynamicFiltersContainerTypes } =
      useRouterUtils();
    const { updateStoreWithInitialDynamicFilters } = useContainerDynamicFiltersAfterEachUiGuard();

    guardResolveResults?.forEach((resolveActionResults, toMatchedRoute) => {
      resolveActionResults?.forEach((resolveActionResult) => {
        if (resolveActionResult?.routeName) {
          if (resolveActionResult.pagination) {
            allPaginationContainerTypes.forEach((containerType) => {
              if (hasRouteContainer(toMatchedRoute, containerType))
                updatePaginationStateData(resolveActionResult.pagination, containerType);
              else if (hasRouteInnerContainer(toMatchedRoute, containerType))
                updatePaginationStateData(resolveActionResult.pagination, containerType);
            });
          }
          allDynamicFiltersContainerTypes.forEach((containerType) => {
            if (hasRouteContainer(toMatchedRoute, containerType))
              updateStoreWithInitialDynamicFilters(resolveActionResult?.initialDynamicFilters, containerType);
          });

          //   allPaginationContainerTypes.forEach((containerType) => {
          //     if (hasRouteContainer(toMatchedRoute, containerType)) {
          //       if (resolveActionResult?.initialFiltersFromUserPreferences)
          //         updateStoreWithInitialFilters(resolveActionResult?.initialFiltersFromUserPreferences, containerType);
          //     }
          //   });
        }
      });
    });
  };

  const resetStoreForContainer = (containerType: AppCoreEnums.ContainerType) => {
    const coreStore = useCoreStore();
    coreStore.getTableBasedContainerData(containerType).value?.reset();
  };

  const prepareUIStoreForContainer = (
    containerType: AppCoreEnums.ContainerType,
    to: RouteLocationNormalized,
    from: RouteLocationNormalized
  ) => {
    const { getRouteWithContainer, getRouteWithParentContainer, replaceRouteParamsWithValues } = useRouterUtils();
    const containersBasedOnParentRoutes = [AppCoreEnums.ContainerType.TabsContainer];
    const useParentContainerRoute = containersBasedOnParentRoutes.includes(containerType);

    const toContainerRoute = useParentContainerRoute
      ? getRouteWithParentContainer(to, containerType)
      : getRouteWithContainer(to, containerType);

    const fromContainerRoute = useParentContainerRoute
      ? getRouteWithParentContainer(from, containerType)
      : getRouteWithContainer(from, containerType);
    const weNavigateToContainerRoute = !!toContainerRoute;
    const weNavigateFromContainerRoute = !!fromContainerRoute;
    const weNavigateAwayFromContainerRoute = weNavigateFromContainerRoute && !weNavigateToContainerRoute;
    const weNavigateToAnotherSameContainerTypeRoute =
      toContainerRoute &&
      fromContainerRoute &&
      replaceRouteParamsWithValues(fromContainerRoute.path, from.params) !=
        replaceRouteParamsWithValues(toContainerRoute?.path, to.params);

    if (weNavigateAwayFromContainerRoute || weNavigateToAnotherSameContainerTypeRoute) {resetStoreForContainer(containerType);}
    if (weNavigateToContainerRoute) {
      initStoreForContainer(containerType, to);
    }
    if ((to.meta as IRouteMetaResolveResults)?.containerType === containerType)
      updateStoreBasedOnRouteResolveActionResults((to.meta as IRouteMetaResolveResults)?.guardResolveResults);
  };

  Object.keys(AppCoreEnums.ContainerType).forEach((containerType) => {
    if (isNaN(Number(containerType)))
      prepareUIStoreForContainer(AppCoreEnums.ContainerType[containerType as keyof typeof AppCoreEnums.ContainerType], to, from);
  });

  // const updateStoreWithInitialFilters = (initialFilters: IFilterItem[], containerType: ContainerType) => {
  //   const coreStore = useCoreStore();

  //   switch (containerType) {
  //     case ContainerType.CollapsibleTableContainer:
  //       coreStore.collapsibleTableContainerFilters = initialFilters;
  //       break;

  //     case ContainerType.TableContainer:
  //       coreStore.tableContainerFilters = initialFilters;
  //       break;
  //   }
  // };
};
